import * as z from "zod";

export const ComponentStatusEnum = z.enum([
  "active",
  "inactive",
  "draft",
  "needs_coating",
  "coating_complete",
  "discontinued",
]);

export const ComponentTypeEnum = z.enum([
  "manufactured",
  "purchased",
  "hybrid",
]);

// Helper for numeric transformation (string input -> number output)
const numericField = (fieldName: string) =>
  z
    .union([z.string(), z.number()])
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      const num = typeof val === "string" ? parseFloat(val) : val;
      if (isNaN(num)) throw new Error(`Invalid ${fieldName}`);
      return num;
    })
    .refine(
      (val) => val === undefined || val >= 0,
      `${fieldName} must be positive`
    )
    .optional();

// Helper for integer fields
const integerField = (fieldName: string, min = 1) =>
  z
    .union([z.string(), z.number()])
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      const num = typeof val === "string" ? parseInt(val, 10) : val;
      if (isNaN(num)) throw new Error(`Invalid ${fieldName}`);
      return num;
    })
    .refine(
      (val) => val === undefined || val >= min,
      `${fieldName} must be at least ${min}`
    )
    .optional();

export const ComponentSchema = z.object({
  id: z.string().uuid(),
  name: z
    .string()
    .min(1, "Name is required")
    .max(255, "Name must be less than 255 characters"),
  description: z
    .string()
    .max(1000, "Description must be less than 1000 characters")
    .optional(),
  component_code: z
    .string()
    .max(100, "Component code must be less than 100 characters")
    .optional(),
  type: ComponentTypeEnum.default("manufactured"),

  // Pricing - transform to numbers to match database/TypeScript types
  cost_price: numericField("Cost price"),
  sell_price: numericField("Sell price"),

  // Manufacturing relationships
  material_id: z.string().uuid().optional(),
  equipment_id: z.string().uuid().optional(),
  job_task_id: z.string().uuid().optional(),

  // Coating information
  coating_required: z.boolean().default(false),
  coating_supplier_id: z.string().uuid().optional(),
  coating_cost: numericField("Coating cost"),
  coating_lead_time: z
    .string()
    .max(50, "Coating lead time must be less than 50 characters")
    .optional(),

  // Purchasing information (for purchased/hybrid components)
  supplier_id: z.string().uuid().optional(),
  supplier_part_number: z
    .string()
    .max(100, "Supplier part number must be less than 100 characters")
    .optional(),
  lead_time: z
    .string()
    .max(50, "Lead time must be less than 50 characters")
    .optional(),
  minimum_order_quantity: integerField("Minimum order quantity", 1),

  // Physical properties - transform to numbers to match database/TypeScript types
  weight: numericField("Weight"),
  width: numericField("Width"),
  height: numericField("Height"),
  length: numericField("Length"),

  // Media
  images: z.array(z.string().url()).optional().default([]),
  technical_drawings: z.array(z.string().url()).optional().default([]),

  // Additional information
  notes: z.string().optional(),
  status: ComponentStatusEnum.default("active"),

  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

export const createComponentSchema = ComponentSchema.omit({
  id: true,
  created: true,
  updated: true,
});

export const updateComponentSchema = ComponentSchema.partial().omit({
  id: true,
  created: true,
  updated: true,
});

// Supplier component pricing schema
export const SupplierComponentSchema = z.object({
  id: z.string().uuid().optional(),
  supplier_id: z.string().uuid(),
  component_id: z.string().uuid(),
  supplier_part_number: z
    .string()
    .max(100, "Supplier part number must be less than 100 characters")
    .nullable()
    .optional(),
  unit_price: z
    .number()
    .min(0, "Unit price cannot be negative")
    .multipleOf(0.01, "Unit price must have at most 2 decimal places")
    .nullable()
    .optional(),
  minimum_quantity: z
    .number()
    .int("Minimum quantity must be a whole number")
    .min(0, "Minimum quantity cannot be negative")
    .nullable()
    .optional(),
  lead_time_days: z
    .number()
    .int("Lead time days must be a whole number")
    .min(0, "Lead time days cannot be negative")
    .nullable()
    .optional(),
  is_preferred: z.boolean().default(false),
});

export const createSupplierComponentSchema = SupplierComponentSchema.omit({
  id: true,
});

export const updateSupplierComponentSchema =
  SupplierComponentSchema.partial().omit({
    id: true,
  });

// Type exports
export type CreateComponentInput = z.infer<typeof createComponentSchema>;
export type UpdateComponentInput = z.infer<typeof updateComponentSchema>;
